import { FieldValues, Path, SubmitHandler, useForm } from 'react-hook-form'
import { FilledButton } from 'components/Custom/FilledButton'
import { SidebarBox } from 'components/Custom/SidebarBox'
import { SideBarContainer } from 'components/Custom/SideBarContainer'
import { SidebarFooter } from 'components/Custom/SidebarFooter'
import { SidebarHeader } from 'components/Custom/SidebarHeader/Web/index'

import { Box } from 'components/Material/index'
import { AppliedFilterChips } from 'components/Custom/AppliedFiltersChip'
import { useState, useEffect } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { ChipData } from 'types/filters'
import { LabelText } from '../Typographies'
import useSidebarStore from 'store/useSidebar'
import { useIsMobile } from 'hooks/useIsResponsive'
import { SidebarBody } from '../SidebarBody'
import { FormConstrutorInputs } from './FormConstrutorInputs'
import { TypeOf, ZodSchema } from 'zod'
import { FilterConfigType } from 'enums/filters'

export interface IField<T extends FieldValues> {
  name: Path<T>
  label: string
  type: 'text' | 'select' | 'date' | 'number' | 'document' | 'doubleNumber'
  maxLength?: number
  minLength?: number
  options?: { label: string; value: string }[]
  doubleField?: {
    nameInicial: Path<T>
    labelInicial: string
    nameFinal: Path<T>
    labelFinal: string
  }
}

export interface SectionFilter<T extends FieldValues> {
  sectionTitle: string
  fields: IField<T>[]
}

export interface FilterSidebarProps<T extends ZodSchema> {
  sections: SectionFilter<TypeOf<T>>[]
  schema: T
  defaultFilter: TypeOf<T>
  currentFilter: TypeOf<T>
  onSubmit: (data: TypeOf<T>) => void
  filterConfig: FilterConfigType
}

export const FilterSidebar = <T extends ZodSchema>({
  sections,
  schema,
  defaultFilter,
  onSubmit,
  currentFilter,
  filterConfig
}: FilterSidebarProps<T>) => {
  const { closeSidebar } = useSidebarStore((state) => state)
  const isMobile = useIsMobile()
  const [appliedFilters, setAppliedFilters] = useState<ChipData[]>([])

  const {
    control,
    getValues,
    setValue,
    reset,
    handleSubmit,
    watch,
    trigger,
    formState: { errors }
  } = useForm<TypeOf<T>>({
    resolver: zodResolver(schema),
    defaultValues: currentFilter,
    mode: 'all'
  })

  useEffect(() => {
    if (watch) {
      watch(() => handleFilter())
      handleFilter()
    }
  }, [watch])
  function getNestedValue(obj: TypeOf<T>, path: string) {
    return path.split('.').reduce((acc, part) => (acc ? acc[part] : undefined), obj)
  }

  function shouldSkipFilter(filterKey: string, values: TypeOf<T>, associatedFields: string[]) {
    return (
      (filterKey === 'st_status' && values[filterKey] === defaultFilter[filterKey]) ||
      associatedFields.every((associatedField) => !values[associatedField])
    )
  }

  function checkNestedFilter(key: string, values: TypeOf<T>): boolean {
    const nestedObj = values[key]
    if (!nestedObj) return false

    const dateSection = sections.find((section) =>
      section.fields.some((field) => field.type === 'date' && field.name === key)
    )

    if (dateSection) {
      const field = dateSection.fields.find((f) => f.name === key)
      if (!field?.doubleField) return false

      const { nameInicial, nameFinal } = field.doubleField

      const startValue = getNestedValue(values, nameInicial)
      const endValue = getNestedValue(values, nameFinal)
      if (!startValue || !endValue) return true

      const defaultStartDate = new Date(getNestedValue(defaultFilter, nameInicial))
      const defaultEndDate = new Date(getNestedValue(defaultFilter, nameFinal))
      const startDate = new Date(startValue)
      const endDate = new Date(endValue)

      return !(
        startDate.getTime() === defaultStartDate.getTime() &&
        endDate.getTime() === defaultEndDate.getTime()
      )
    }

    const initialValue = nestedObj[`${key}_inicial`]
    const finalValue = nestedObj[`${key}_final`]

    return (
      (initialValue !== null && initialValue !== undefined && initialValue !== '') ||
      (finalValue !== null && finalValue !== undefined && finalValue !== '')
    )
  }

  function handleFilter() {
    const values = getValues()
    const newAppliedFilters: ChipData[] = []
    Object.keys(filterConfig).forEach((field) => {
      const filterConfigCheck = filterConfig[field]
      const associatedFields = filterConfig[field].fields

      if (filterConfigCheck.isNested) {
        if (checkNestedFilter(field, values)) {
          newAppliedFilters.push({
            key: field,
            label: filterConfigCheck.label
          })
        }
      } else {
        if (!shouldSkipFilter(field, values, associatedFields)) {
          newAppliedFilters.push({
            key: field,
            label: filterConfigCheck.label
          })
        }
      }
    })

    setAppliedFilters(newAppliedFilters)
  }

  const handleRemoveFilterChip = (filterField: ChipData) => {
    const newAppliedFilters = appliedFilters.filter((filter) => filter.key !== filterField.key)
    setAppliedFilters(newAppliedFilters)
    const key = filterField.key
    const fields = filterConfig[filterField.key].fields

    fields.forEach((field) => {
      if (filterConfig[key].isNested) {
        const setValueKey = `${key}.${field}`
        setValue(setValueKey as Path<TypeOf<T>>, defaultFilter[key][field])
      } else {
        setValue(field as Path<TypeOf<T>>, defaultFilter[field])
      }
    })
    trigger()
  }

  const handleSubmitForm: SubmitHandler<TypeOf<T>> = async (data) => {
    onSubmit(data)

    if (isMobile) {
      closeSidebar()
    }
  }
  const handleFormSubmit = handleSubmit(handleSubmitForm)

  const handleResetFilter = () => {
    reset(defaultFilter)
    onSubmit(defaultFilter)
    handleFilter()
  }

  return (
    <form onSubmit={handleFormSubmit}>
      <SideBarContainer>
        <SidebarHeader title="Filtros" onClick={() => closeSidebar()} hideCloseButton={!isMobile} />

        <SidebarBox>
          <Box className="pt-5 flex justify-center w-full">
            <Box className="w-full max-w-[390px]">
              <AppliedFilterChips
                appliedFilters={appliedFilters}
                clearAllFilter={handleResetFilter}
                onRemove={handleRemoveFilterChip}
              />
            </Box>
          </Box>
        </SidebarBox>
        <SidebarBody className="p-5 flex flex-col flex-grow overflow-auto w-full items-center space-y-5  ">
          {sections.map((section, sectionIndex) => (
            <SidebarBox
              key={sectionIndex}
              className="space-y-5 max-w-[390px]"
              divider={sectionIndex !== sections.length - 1}
            >
              <LabelText text={section.sectionTitle} typeStyle="large" />
              <>
                {section.fields.map((field) => (
                  <FormConstrutorInputs
                    key={field.name}
                    control={control}
                    field={field}
                    errors={errors}
                  />
                ))}
              </>
            </SidebarBox>
          ))}
        </SidebarBody>
        <SidebarFooter>
          <FilledButton loading={false} type="submit">
            Aplicar Filtros
          </FilledButton>
        </SidebarFooter>
      </SideBarContainer>
    </form>
  )
}
